import { useMemo, type ReactNode } from 'react'

import { useSession } from '@/auth/session'
import { gruposPorId } from '@/permissions/gruposFixture'
import { resolverPermissoes } from '@/permissions/resolver'
import { ContextoDePermissoes, usePermission } from '@/permissions/usePermission'

/**
 * Resolves the permission map once per session — story 0.4 AC-1.
 *
 * 🔒 It deliberately does **not** depend on the selected client. Grupos are global
 * and carry no client reference (BR-18), so switching tenant cannot change what a
 * user may do — and making the resolve depend on `idCliente` would quietly imply
 * the opposite to the next reader.
 */
export function PermissionProvider({ children }: { children: ReactNode }) {
  const { sessao, perfil } = useSession()

  /**
   * Duas origens para os mesmos grupos, e a ordem entre elas não é arbitrária.
   *
   * `perfil.grupos` vem de `GET /eu` — permissão como DADO, servida pelo backend e
   * editável por um Administrador. É a origem verdadeira e ganha sempre que existe.
   *
   * `gruposFixture` é a transcrição da matriz de `docs/PERSONAS.md` compilada dentro
   * do bundle, e sobrevive apenas para o modo mock, onde não há `/eu` a consultar.
   * ⚠️ Ela NÃO é fallback do modo Cognito: se o `/eu` falhar lá, o usuário fica sem
   * permissão e a tela diz por quê. Cair na fixture nesse caso concederia acesso a
   * partir de um arquivo do cliente justamente quando o servidor não respondeu — que
   * é a direção perigosa do erro.
   */
  const daApi = perfil?.grupos
  const chave = (sessao?.grupos ?? []).join('|')
  const mapa = useMemo(
    () => resolverPermissoes(daApi ?? gruposPorId(chave === '' ? [] : chave.split('|'))),
    [daApi, chave],
  )

  return <ContextoDePermissoes.Provider value={mapa}>{children}</ContextoDePermissoes.Provider>
}

/**
 * Gates a fragment on one permission — AC-4.
 *
 * `<Can ver="finops.oportunidades">` for presence; `<Can editar="…">` for the
 * per-row edit icon, the *visível ao cliente* toggle and every save button.
 *
 * Exactly one of `ver` / `editar` is expected. Passing both would read as "and",
 * which `editar` already implies (`escrita` includes `leitura`), so the type keeps
 * them apart rather than inviting the question.
 */
type CanProps = { children: ReactNode; fallback?: ReactNode } & (
  { ver: string; editar?: never } | { editar: string; ver?: never }
)

export function Can({ children, fallback = null, ...props }: CanProps) {
  const pedeVer = 'ver' in props && Boolean(props.ver)
  const acesso = usePermission(pedeVer ? props.ver! : props.editar!)

  return <>{(pedeVer ? acesso.ver : acesso.editar) ? children : fallback}</>
}
